import { Box, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React, { useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import useToken from '../../services/token';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenStreetMaps from '../map/OpenStreetMaps';
import TechCardBlock from './TechCardBlock';
import { canEditRecords, NO_PERMISSION_TOOLTIP } from '../../services/permissions';
const FieldInfoPanel = forwardRef((props, ref) => {
    const [detail, setDetail] = React.useState(null);
    const [passport, setPassport] = React.useState(null);
    // Ширина и позиция мыши синхронизируются на каждом шаге драга (а не
    // фиксируются один раз в момент mousedown) — если родитель обрежет
    // запрошенную ширину лимитом, следующий шаг посчитается от факта,
    // а не от устаревшего плана. Так ползунок не убегает от края панели.
    const widthRef = React.useRef(props.width);
    widthRef.current = props.width;
    const lastXRef = React.useRef(0);
    const onResizeRef = React.useRef(props.onResize);
    onResizeRef.current = props.onResize;
    const handleMouseMove = React.useCallback((e) => {
        const delta = e.clientX - lastXRef.current;
        lastXRef.current = e.clientX;
        onResizeRef.current(widthRef.current + delta);
    }, []);
    const handleMouseUp = React.useCallback(() => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }, [handleMouseMove]);
    const startResize = (e) => {
        e.preventDefault();
        lastXRef.current = e.clientX;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };
    const { parseJwt } = useToken();
    const role = React.useMemo(() => {
        var _a;
        const payload = parseJwt(props.token);
        return (_a = payload === null || payload === void 0 ? void 0 : payload.role) !== null && _a !== void 0 ? _a : null;
    }, [props.token]);
    const canEdit = canEditRecords(role);
    const currentSeasonInfo = React.useMemo(() => {
        var _a, _b;
        if (!passport)
            return null;
        return (_b = (_a = passport.seasons.find(s => s.season === props.season)) !== null && _a !== void 0 ? _a : passport.seasons[0]) !== null && _b !== void 0 ? _b : null;
    }, [passport, props.season]);
    const seasonDescription = [
        currentSeasonInfo === null || currentSeasonInfo === void 0 ? void 0 : currentSeasonInfo.culture_name,
        (currentSeasonInfo === null || currentSeasonInfo === void 0 ? void 0 : currentSeasonInfo.grade_name) ? `Сорт: ${currentSeasonInfo.grade_name}` : null,
    ].filter(Boolean).join(' · ');
    const [editOpen, setEditOpen] = React.useState(false);
    const [editSaving, setEditSaving] = React.useState(false);
    const [editName, setEditName] = React.useState('');
    const [editLat, setEditLat] = React.useState(0);
    const [editLng, setEditLng] = React.useState(0);
    const [editAlt, setEditAlt] = React.useState(0);
    const [redrawMode, setRedrawMode] = React.useState(false);
    const [redrawJson, setRedrawJson] = React.useState(null);
    const redrawMap = React.useRef(null);
    useImperativeHandle(ref, () => ({}));
    const loadAll = () => {
        if (!props.token)
            return;
        axios.get(`/field/detail/${props.id}`, { headers: { "Authorization": `Bearer ${props.token}` } }).then((r) => {
            if (r.data)
                setDetail(r.data);
        });
        axios.get(`/field/passport/${props.id}`, { headers: { "Authorization": `Bearer ${props.token}` } }).then((r) => {
            if (r.data)
                setPassport(r.data);
        });
    };
    React.useEffect(() => {
        loadAll();
    }, [props.id]);
    const openEdit = () => {
        var _a;
        if (!detail || !passport)
            return;
        setEditName(detail.name);
        setEditLat(passport.latitude_center);
        setEditLng(passport.longitude_center);
        setEditAlt((_a = passport.altitude_center) !== null && _a !== void 0 ? _a : 0);
        setEditOpen(true);
    };
    const saveEdit = () => {
        setEditSaving(true);
        axios.put(`/field/update/${props.id}`, {
            name: editName,
            latitude_center: editLat,
            longitude_center: editLng,
            altitude_center: editAlt,
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${props.token}`
            }
        })
            .then((response) => {
            var _a;
            if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.result) {
                setEditOpen(false);
                loadAll();
                if (props.onUpdated)
                    props.onUpdated();
            }
        })
            .finally(() => setEditSaving(false));
    };
    const saveGeometry = () => {
        if (!redrawJson)
            return;
        redrawJson.id = 0;
        let geo = { type: "FeatureCollection", features: [redrawJson] };
        let build = JSON.parse(JSON.stringify(geo));
        if (build.features.length > 0) {
            let coords = build.features[0].geometry.coordinates.map((c) => [c[1], c[0]]);
            const firstPoint = coords[0];
            const lastPoint = coords[coords.length - 1];
            if (coords.length > 0 && (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1])) {
                coords.push([...firstPoint]);
            }
            build.features[0].geometry.coordinates = [coords];
        }
        const formData = new FormData();
        formData.append("geo", JSON.stringify(build));
        setEditSaving(true);
        axios.put(`/field/geometry/${props.id}`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
                "Authorization": `Bearer ${props.token}`
            }
        })
            .then((response) => {
            var _a;
            if ((_a = response.data) === null || _a === void 0 ? void 0 : _a.success) {
                setRedrawMode(false);
                setRedrawJson(null);
                setEditOpen(false);
                loadAll();
                if (props.onGeometryUpdated)
                    props.onGeometryUpdated(props.id);
                else if (props.onUpdated)
                    props.onUpdated();
            }
        })
            .finally(() => setEditSaving(false));
    };
    if (props.collapsed) {
        return (React.createElement(Box, { sx: {
                width: 28, height: '100%', display: 'flex', alignItems: 'flex-start',
                justifyContent: 'center', pt: 1, backgroundColor: '#f7f9f7',
                boxShadow: '2px 0 6px rgba(0,0,0,0.2)'
            } },
            React.createElement(Tooltip, { title: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u043F\u0430\u0441\u043F\u043E\u0440\u0442 \u043F\u043E\u043B\u044F" },
                React.createElement(IconButton, { size: "small", onClick: props.onToggleCollapse, sx: { color: '#2e7d32' } },
                    React.createElement(ChevronRightIcon, { fontSize: "small" })))));
    }
    return (React.createElement(Box, { sx: {
            width: props.width, height: '100%', display: 'flex', flexDirection: 'column',
            position: 'relative', backgroundColor: '#f7f9f7', boxShadow: '2px 0 6px rgba(0,0,0,0.2)'
        } },
        React.createElement(Box, { onMouseDown: startResize, sx: {
                position: 'absolute', right: '-3px', top: 0, bottom: 0, width: '6px',
                cursor: 'col-resize', zIndex: 1,
                backgroundColor: 'transparent',
                transition: 'background-color 0.15s',
                '&:hover': { backgroundColor: 'rgba(18, 113, 71, 0.35)' }
            } }),
        detail !== null && passport !== null && (React.createElement(Card, { sx: { border: 'none', boxShadow: 'none', borderRadius: 0, display: 'flex', flexDirection: 'column', flex: '1 1 auto', minWidth: 0, backgroundColor: '#f7f9f7' } },
            React.createElement(CardContent, { sx: { px: 2, py: 1.5, color: '#24352a', backgroundColor: '#f7f9f7', position: 'relative', borderBottom: '1px solid #e2e8e3' } },
                React.createElement(IconButton, { size: "small", onClick: props.onToggleCollapse, sx: { position: 'absolute', top: 7, right: 7, color: '#2e7d32' } },
                    React.createElement(ChevronLeftIcon, { fontSize: "small" })),
                React.createElement(Typography, { variant: "subtitle1", sx: { fontWeight: 700, lineHeight: 1.25, pr: 4 } }, "\u0422\u0435\u0445\u043D\u043E\u043B\u043E\u0433\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u043A\u0430\u0440\u0442\u0430"),
                React.createElement(Stack, { direction: "row", spacing: 1.25, useFlexGap: true, flexWrap: "wrap", sx: { alignItems: 'baseline', mt: 0.6 } },
                    React.createElement(Typography, { variant: "body2", sx: { fontWeight: 600, color: '#43564a' } },
                        detail.hkr.toFixed(2),
                        " \u0433\u0430"),
                    seasonDescription && (React.createElement(Typography, { variant: "caption", sx: { color: '#637368' } }, seasonDescription)))),
            React.createElement(Box, { sx: { flex: '1 1 auto', overflowY: 'auto', minWidth: 0, minHeight: 0, backgroundColor: '#f7f9f7' } }, props.season
                ? React.createElement(TechCardBlock, { token: props.token, fieldId: props.id, season: props.season })
                : (React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { p: 2, display: 'block' } }, "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0441\u0435\u0437\u043E\u043D \u0432 \u0441\u043F\u0438\u0441\u043A\u0435 \u043F\u043E\u043B\u0435\u0439 \u0441\u043B\u0435\u0432\u0430"))),
            React.createElement(Box, { sx: { px: 1.5, py: 0.75, minWidth: 0, backgroundColor: '#f7f9f7', borderTop: '1px solid #e2e8e3' } },
                React.createElement(Stack, { direction: "row", spacing: 0.75, justifyContent: "center" },
                    React.createElement(Tooltip, { title: canEdit ? 'Редактировать поле' : NO_PERMISSION_TOOLTIP },
                        React.createElement("span", null,
                            React.createElement(IconButton, { size: "small", color: "primary", disabled: !canEdit, onClick: openEdit, sx: { border: '1px solid', borderColor: 'divider' } },
                                React.createElement(EditIcon, { fontSize: "small" })))),
                    React.createElement(Tooltip, { title: !canEdit ? NO_PERMISSION_TOOLTIP : detail.has_related_data ? 'Нельзя удалить поле, пока есть связанные данные' : 'Удалить поле' },
                        React.createElement("span", null,
                            React.createElement(IconButton, { size: "small", color: "error", disabled: !canEdit || detail.has_related_data, onClick: () => props.delete(props.id, detail.relations), sx: { border: '1px solid', borderColor: 'divider' } },
                                React.createElement(DeleteOutlineIcon, { fontSize: "small" })))))))),
        React.createElement(Dialog, { open: editOpen, onClose: () => setEditOpen(false), maxWidth: "sm", fullWidth: true },
            React.createElement(DialogTitle, null, "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u043E\u0432\u0430\u0442\u044C \u043F\u043E\u043B\u0435"),
            React.createElement(DialogContent, null,
                React.createElement(Stack, { spacing: 2, sx: { mt: 1 } },
                    React.createElement(TextField, { label: "\u041D\u0430\u0438\u043C\u0435\u043D\u043E\u0432\u0430\u043D\u0438\u0435", variant: "filled", fullWidth: true, value: editName, onChange: (e) => setEditName(e.target.value) }),
                    React.createElement(Button, { size: "small", variant: redrawMode ? "contained" : "outlined", onClick: () => setRedrawMode(!redrawMode) }, redrawMode ? 'Отменить перерисовку' : 'Перерисовать границы поля'),
                    redrawMode && (React.createElement(Box, { sx: { height: 400, border: '1px dashed grey' } },
                        React.createElement(OpenStreetMaps, { ref: redrawMap, token: props.token, url: `/field/get/${props.id}`, editMode: { polygon: true, circlemarker: false, remove: true }, setJson: setRedrawJson, item: 0 }))),
                    !redrawMode && (React.createElement(Stack, { direction: "row", spacing: 2 },
                        React.createElement(TextField, { label: "\u0428\u0438\u0440\u043E\u0442\u0430", variant: "filled", type: "number", fullWidth: true, value: editLat, onChange: (e) => setEditLat(parseFloat(e.target.value) || 0) }),
                        React.createElement(TextField, { label: "\u0414\u043E\u043B\u0433\u043E\u0442\u0430", variant: "filled", type: "number", fullWidth: true, value: editLng, onChange: (e) => setEditLng(parseFloat(e.target.value) || 0) }),
                        React.createElement(TextField, { label: "\u0412\u044B\u0441\u043E\u0442\u0430", variant: "filled", type: "number", fullWidth: true, value: editAlt, onChange: (e) => setEditAlt(parseFloat(e.target.value) || 0) }))))),
            React.createElement(DialogActions, null,
                redrawMode
                    ? React.createElement(Button, { disabled: editSaving || !redrawJson, onClick: saveGeometry }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0433\u0440\u0430\u043D\u0438\u0446\u044B")
                    : React.createElement(Button, { disabled: editSaving, onClick: saveEdit }, "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C"),
                React.createElement(Button, { disabled: editSaving, onClick: () => { setEditOpen(false); setRedrawMode(false); setRedrawJson(null); } }, "\u041E\u0442\u043C\u0435\u043D\u0430")))));
});
export default FieldInfoPanel;
